import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ChevronUp, Coins, Heart, Minus, Plus, Star, ZoomIn } from "lucide-react";
import clsx from "clsx";
import { Badge, EmptyState, PillGroup, PriceTag, Skeleton, getDiscountPercent, toast } from "@aurelia/ui";
import { useProduct } from "../../features/catalog/queries";
import { useProductReviews, useCreateReview } from "../../features/reviews/queries";
import { useAddToCart } from "../../features/cart/mutations";
import { useWishlist, useToggleWishlist } from "../../features/wishlist/queries";
import { useAuthStore } from "../../lib/stores/auth";
import { useDocumentMeta } from "../../lib/useDocumentMeta";
import { useJsonLd } from "../../lib/useJsonLd";
import { SITE_URL } from "../../lib/seoConstants";
import { recordRecentlyViewed } from "../../lib/useRecentlyViewed";
import { ImageZoomLightbox } from "../../components/product/ImageZoomLightbox";
import { RelatedProducts } from "../../components/product/RelatedProducts";
import { ProductDeliveryInfo } from "../../components/product/ProductDeliveryInfo";
import { RecentlyViewedSection } from "../../components/product/RecentlyViewedSection";
import { ProductTabs } from "../../components/product/ProductTabs";
import { ProductTrustGrid } from "../../components/product/ProductTrustGrid";
import { ProductShareLinks } from "../../components/product/ProductShareLinks";

export function ProductPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useProduct(slug);
  const product = data?.product;
  const redirectSlug = data?.redirect;

  // The requested slug used to belong to this product but was renamed —
  // move to the current URL instead of showing "not found". `replace` so
  // the dead old slug doesn't sit in browser history.
  useEffect(() => {
    if (redirectSlug) navigate(`/product/${redirectSlug}`, { replace: true });
  }, [redirectSlug, navigate]);
  useDocumentMeta(
    product?.name ?? "Product",
    (product?.shortDescription || product?.description)?.slice(0, 155) || undefined,
    product?.seoKeywords || undefined,
    product?.images?.[0],
    { ogType: "product" }
  );
  useJsonLd(
    "breadcrumb-jsonld",
    product
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            ...(product.category
              ? [
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: product.category.name,
                    item: `${SITE_URL}/category/${product.category.slug}`
                  }
                ]
              : []),
            {
              "@type": "ListItem",
              position: product.category ? 3 : 2,
              name: product.name,
              item: `${SITE_URL}/product/${product.slug}`
            }
          ]
        }
      : null
  );
  const { data: reviews } = useProductReviews(product?.id);
  const addToCart = useAddToCart();
  const createReview = useCreateReview(product?.id ?? "");
  const isAuthenticated = useAuthStore((s) => s.status === "authenticated");
  const { data: wishlist } = useWishlist();
  const toggleWishlist = useToggleWishlist();
  const wishlisted = !!product && !!wishlist?.some((p) => p.id === product.id);

  const avgRating = reviews && reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : null;

  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [thumbStart, setThumbStart] = useState(0);
  const VISIBLE_THUMBS = 5;
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [zoomOpen, setZoomOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const addToCartRef = useRef<HTMLDivElement>(null);

  const sizes = useMemo(
    () => Array.from(new Set(product?.variants.map((v) => v.size).filter(Boolean))) as string[],
    [product]
  );
  const colors = useMemo(
    () => Array.from(new Set(product?.variants.map((v) => v.color).filter(Boolean))) as string[],
    [product]
  );

  useEffect(() => {
    if (!product) return;
    const first = product.variants[0];
    setSelectedSize(first?.size ?? null);
    setSelectedColor(first?.color ?? null);
    setSelectedImageIndex(0);
    setThumbStart(0);
  }, [product]);

  useEffect(() => {
    if (!product) return;
    recordRecentlyViewed({
      slug: product.slug,
      name: product.name,
      imageUrl: product.images[0] ?? null,
      basePrice: product.basePrice,
      compareAtPrice: product.compareAtPrice
    });
  }, [product]);

  useEffect(() => {
    const el = addToCartRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => entry && setShowStickyBar(!entry.isIntersecting), {
      threshold: 0
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [product]);

  const selectedVariant = useMemo(
    () => product?.variants.find((v) => v.size === selectedSize && v.color === selectedColor),
    [product, selectedSize, selectedColor]
  );

  const price = selectedVariant?.priceOverride ?? product?.basePrice ?? 0;
  const inStock = (selectedVariant?.stock ?? 0) > 0;
  const discountPercent = getDiscountPercent(price, product?.compareAtPrice);

  // Switching variants can change (or remove) the available stock — clamp
  // instead of silently letting a stale quantity exceed the new limit.
  useEffect(() => {
    setQuantity((q) => Math.min(Math.max(q, 1), Math.max(selectedVariant?.stock ?? 1, 1)));
  }, [selectedVariant]);

  useJsonLd(
    "product-jsonld",
    product
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          description: product.description || product.shortDescription || undefined,
          image: product.images,
          sku: selectedVariant?.sku,
          url: `${SITE_URL}/product/${product.slug}`,
          offers: {
            "@type": "Offer",
            url: `${SITE_URL}/product/${product.slug}`,
            priceCurrency: "PKR",
            price: (price / 100).toFixed(2),
            availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
          },
          // Real data only — computed from the actual fetched reviews, never
          // rendered until reviews have loaded and at least one exists.
          ...(reviews && reviews.length > 0
            ? {
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1),
                  reviewCount: reviews.length
                },
                review: reviews.slice(0, 10).map((r) => ({
                  "@type": "Review",
                  author: { "@type": "Person", name: r.authorName },
                  datePublished: r.createdAt,
                  reviewBody: r.text || undefined,
                  reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 }
                }))
              }
            : {})
        }
      : null
  );

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      toast("Sign in to add items to your cart", "danger");
      return;
    }
    if (!selectedVariant) return;
    addToCart.mutate({ variantId: selectedVariant.id, quantity });
  };

  const handleToggleWishlist = () => {
    if (!isAuthenticated) {
      toast("Sign in to save items to your wishlist", "danger");
      return;
    }
    if (!product) return;
    toggleWishlist.mutate(product.id);
  };

  const handleBuyNow = () => {
    if (!isAuthenticated) {
      toast("Sign in to add items to your cart", "danger");
      return;
    }
    if (!selectedVariant) return;
    addToCart.mutate(
      { variantId: selectedVariant.id, quantity },
      { onSuccess: () => navigate("/checkout") }
    );
  };

  if (isLoading || redirectSlug) {
    return (
      <main className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-12">
        <Skeleton className="aspect-square" />
        <div className="flex flex-col gap-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-24 w-full" />
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="max-w-xl mx-auto px-6 py-24">
        <EmptyState
          tone={isError ? "error" : "neutral"}
          title={isError ? "Couldn't load this product" : "Product not found"}
          description={isError ? "Please refresh and try again." : "It may have been removed or the link is incorrect."}
        />
      </main>
    );
  }

  return (
    <main className="max-w-6xl mx-auto px-6 py-16">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-ink-muted mb-6 flex-wrap">
        <Link to="/" className="hover:text-ink transition-colors duration-200">
          Home
        </Link>
        {product.category && (
          <>
            <ChevronRight size={14} className="shrink-0" aria-hidden />
            <Link to={`/category/${product.category.slug}`} className="hover:text-ink transition-colors duration-200">
              {product.category.name}
            </Link>
          </>
        )}
        <ChevronRight size={14} className="shrink-0" aria-hidden />
        <span className="text-ink truncate">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
        <div className="flex flex-col-reverse md:flex-row gap-4 min-w-0 md:items-start">
          {product.images.length > 1 && (
            <>
              {/* Mobile: swipeable strip of every image, no paging arrows needed. */}
              <div className="flex md:hidden gap-3 overflow-x-auto no-scrollbar w-full min-w-0">
                {product.images.map((url, i) => (
                  <button
                    key={url}
                    type="button"
                    onClick={() => setSelectedImageIndex(i)}
                    aria-label={`View image ${i + 1}`}
                    className={clsx(
                      "h-16 w-16 rounded-lg overflow-hidden border-2 transition-colors duration-200 shrink-0",
                      i === selectedImageIndex ? "border-accent" : "border-transparent hover:border-border"
                    )}
                  >
                    <img src={url} alt="" className="w-full h-full object-contain object-center" />
                  </button>
                ))}
              </div>

              {/* Desktop: fixed-height column, paged 5 at a time via arrows. */}
              <div className="hidden md:flex flex-col items-center gap-2 shrink-0">
                <button
                  type="button"
                  aria-label="Show previous images"
                  disabled={thumbStart === 0}
                  onClick={() => setThumbStart((s) => Math.max(0, s - 1))}
                  className="h-6 w-16 rounded-lg flex items-center justify-center text-ink-muted hover:text-ink hover:bg-surface transition-colors duration-200 disabled:opacity-0 disabled:pointer-events-none"
                >
                  <ChevronUp size={16} />
                </button>

                <div className="flex flex-col gap-3">
                  {product.images.slice(thumbStart, thumbStart + VISIBLE_THUMBS).map((url, i) => {
                    const realIndex = thumbStart + i;
                    return (
                      <button
                        key={url}
                        type="button"
                        onClick={() => setSelectedImageIndex(realIndex)}
                        aria-label={`View image ${realIndex + 1}`}
                        className={clsx(
                          "h-16 w-16 rounded-lg overflow-hidden border-2 transition-colors duration-200 shrink-0",
                          realIndex === selectedImageIndex ? "border-accent" : "border-transparent hover:border-border"
                        )}
                      >
                        <img src={url} alt="" className="w-full h-full object-contain object-center" />
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  aria-label="Show more images"
                  disabled={thumbStart + VISIBLE_THUMBS >= product.images.length}
                  onClick={() => setThumbStart((s) => Math.min(product.images.length - VISIBLE_THUMBS, s + 1))}
                  className="h-6 w-16 rounded-lg flex items-center justify-center text-ink-muted hover:text-ink hover:bg-surface transition-colors duration-200 disabled:opacity-0 disabled:pointer-events-none"
                >
                  <ChevronDown size={16} />
                </button>
              </div>
            </>
          )}

          <div className="rounded-2xl bg-surface border border-border overflow-hidden relative min-w-0 md:flex-1">
            {product.images.length > 0 ? (
              <>
                {/* A single in-flow image, sized by its own natural aspect
                    ratio — the container takes whatever height that image
                    renders at rather than imposing one of its own. Swapping
                    `key` on select forces a clean remount instead of the
                    browser holding the previous frame while the new image
                    loads. */}
                <img
                  key={product.images[selectedImageIndex]}
                  src={product.images[selectedImageIndex]}
                  alt={product.name}
                  className="w-full h-auto block"
                />
                <button
                  type="button"
                  onClick={() => setZoomOpen(true)}
                  aria-label="Zoom into product image"
                  className="absolute bottom-3 right-3 h-11 w-11 rounded-full glass flex items-center justify-center text-ink hover:scale-110 active:scale-95 transition-transform duration-300 ease-physical"
                >
                  <ZoomIn size={18} />
                </button>
              </>
            ) : (
              <div className="aspect-square w-full flex items-center justify-center text-ink-muted">No image</div>
            )}
          </div>
        </div>

        <ImageZoomLightbox
          open={zoomOpen}
          images={product.images}
          index={selectedImageIndex}
          alt={product.name}
          onClose={() => setZoomOpen(false)}
          onNavigate={setSelectedImageIndex}
        />

        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-2 flex-wrap">
            {product.category && <Badge tone="neutral">{product.category.name}</Badge>}
            {product.isNewArrival && <Badge tone="success">New Arrival</Badge>}
            {product.isBestSeller && <Badge tone="accent">Best Seller</Badge>}
            {product.tags.map((tag) => (
              <Badge key={tag} tone="neutral">
                {tag}
              </Badge>
            ))}
          </div>
          <div>
            <h1 className="text-display-sm">{product.name}</h1>
            {product.shortDescription && (
              <p className="text-ink-muted mt-2">{product.shortDescription}</p>
            )}
          </div>
          {(avgRating !== null || product.soldCount > 0) && (
            <div className="flex items-center gap-2 text-sm -mt-2">
              {avgRating !== null && (
                <>
                  <div className="flex text-accent">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={14} fill={i < Math.round(avgRating) ? "currentColor" : "none"} />
                    ))}
                  </div>
                  <span className="font-medium">{avgRating.toFixed(1)}</span>
                  <span className="text-ink-muted">
                    ({reviews?.length} review{reviews?.length === 1 ? "" : "s"})
                  </span>
                </>
              )}
              {avgRating !== null && product.soldCount > 0 && <span className="text-ink-muted">|</span>}
              {product.soldCount > 0 && <span className="text-ink-muted">Sold {product.soldCount}</span>}
            </div>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <PriceTag cents={price} compareAtCents={product.compareAtPrice ?? undefined} className="text-xl" />
            {discountPercent !== null && <Badge tone="danger">{discountPercent}% OFF</Badge>}
            {product.soldCount > 0 && (
              <span className="text-sm text-ink-muted">{product.soldCount} sold</span>
            )}
          </div>
          {product.rewardCoins > 0 && (
            <div className="flex items-center gap-2 w-fit rounded-full bg-accent-soft text-accent-strong px-4 py-2 text-sm font-medium">
              <Coins size={16} />
              Buy this and earn {product.rewardCoins} coin{product.rewardCoins === 1 ? "" : "s"} for your next order!
            </div>
          )}
          {sizes.length > 0 && (
            <div>
              <p className="text-sm font-medium text-ink-muted mb-2">Size</p>
              <PillGroup
                options={sizes.map((s) => ({ value: s, label: s }))}
                value={selectedSize}
                onChange={setSelectedSize}
                aria-label="Size"
              />
            </div>
          )}

          {colors.length > 0 && (
            <div>
              <p className="text-sm font-medium text-ink-muted mb-2">Color</p>
              <PillGroup
                options={colors.map((c) => ({ value: c, label: c, swatch: c.toLowerCase() }))}
                value={selectedColor}
                onChange={setSelectedColor}
                aria-label="Color"
              />
            </div>
          )}

          {selectedVariant && inStock && selectedVariant.stock <= 5 && (
            <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">Only {selectedVariant.stock} left</p>
          )}

          {inStock && (
            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <p className="text-sm font-medium text-ink-muted mb-2">Quantity</p>
                <div className="inline-flex items-center h-12 rounded-full border border-border">
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    disabled={quantity <= 1}
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="h-full w-12 flex items-center justify-center text-ink-muted hover:text-ink disabled:opacity-30 disabled:pointer-events-none"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="w-10 text-center text-sm font-medium tabular-nums">{quantity}</span>
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    disabled={quantity >= (selectedVariant?.stock ?? 1)}
                    onClick={() => setQuantity((q) => Math.min(selectedVariant?.stock ?? 1, q + 1))}
                    className="h-full w-12 flex items-center justify-center text-ink-muted hover:text-ink disabled:opacity-30 disabled:pointer-events-none"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
              <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                In Stock ({selectedVariant?.stock} available)
              </span>
            </div>
          )}

          <div ref={addToCartRef} className="flex flex-col gap-3">
            <button
              type="button"
              disabled={!inStock || addToCart.isPending}
              onClick={handleAddToCart}
              className="w-full h-14 rounded-full bg-accent text-ink font-medium tracking-tight hover:bg-accent-strong transition-colors duration-300 ease-physical disabled:opacity-40 disabled:pointer-events-none"
            >
              {inStock ? "Add to cart" : "Out of stock"}
            </button>
            <div className="grid grid-cols-2 gap-3">
              {inStock && (
                <button
                  type="button"
                  disabled={addToCart.isPending}
                  onClick={handleBuyNow}
                  className="h-12 rounded-full border-2 border-ink text-ink font-medium tracking-tight hover:bg-surface transition-colors duration-300 ease-physical disabled:opacity-40 disabled:pointer-events-none dark:border-accent dark:text-accent"
                >
                  Buy now
                </button>
              )}
              <button
                type="button"
                onClick={handleToggleWishlist}
                className={clsx(
                  "h-12 rounded-full border font-medium tracking-tight flex items-center justify-center gap-2 transition-colors duration-300 ease-physical",
                  !inStock && "col-span-2",
                  wishlisted ? "border-accent text-accent bg-accent-soft" : "border-border text-ink hover:bg-surface"
                )}
              >
                <Heart size={16} fill={wishlisted ? "currentColor" : "none"} />
                {wishlisted ? "Wishlisted" : "Add to Wishlist"}
              </button>
            </div>
          </div>

          <ProductTrustGrid isReturnable={product.isReturnable} />

          <ProductTabs
            tabs={[
              { id: "description", label: "Description" },
              { id: "additional", label: "Additional Information" },
              { id: "reviews", label: `Reviews (${reviews?.length ?? 0})` },
              { id: "shipping", label: "Shipping & Returns" }
            ]}
          >
            {(tab) => {
              if (tab === "description") {
                return (
                  <div className="grid sm:grid-cols-3 gap-8">
                    <div className="sm:col-span-2">
                      {product.description ? (
                        <p className="text-sm text-ink-muted leading-relaxed whitespace-pre-line">
                          {product.description}
                        </p>
                      ) : (
                        <p className="text-sm text-ink-muted">No description available.</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-4">
                      {selectedVariant?.sku && (
                        <div>
                          <p className="text-xs text-ink-muted">SKU</p>
                          <p className="text-sm font-medium">{selectedVariant.sku}</p>
                        </div>
                      )}
                      {product.category && (
                        <div>
                          <p className="text-xs text-ink-muted">Category</p>
                          <p className="text-sm font-medium">{product.category.name}</p>
                        </div>
                      )}
                      {product.tags.length > 0 && (
                        <div>
                          <p className="text-xs text-ink-muted">Tags</p>
                          <p className="text-sm font-medium">{product.tags.join(", ")}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-ink-muted mb-2">Share</p>
                        <ProductShareLinks
                          productName={product.name}
                          productUrl={`${SITE_URL}/product/${product.slug}`}
                        />
                      </div>
                    </div>
                  </div>
                );
              }
              if (tab === "additional") {
                return (
                  <div className="text-sm divide-y divide-border">
                    {product.category && (
                      <div className="flex justify-between py-2.5">
                        <span className="text-ink-muted">Category</span>
                        <span className="font-medium">{product.category.name}</span>
                      </div>
                    )}
                    {product.tags.length > 0 && (
                      <div className="flex justify-between py-2.5">
                        <span className="text-ink-muted">Tags</span>
                        <span className="font-medium">{product.tags.join(", ")}</span>
                      </div>
                    )}
                    {product.weightGrams != null && (
                      <div className="flex justify-between py-2.5">
                        <span className="text-ink-muted">Weight</span>
                        <span className="font-medium">{product.weightGrams}g</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2.5">
                      <span className="text-ink-muted">Returns</span>
                      <span className="font-medium">{product.isReturnable ? "Eligible — 7 days" : "Final sale"}</span>
                    </div>
                    {sizes.length > 0 && (
                      <div className="flex justify-between py-2.5">
                        <span className="text-ink-muted">Sizes</span>
                        <span className="font-medium">{sizes.join(", ")}</span>
                      </div>
                    )}
                    {colors.length > 0 && (
                      <div className="flex justify-between py-2.5">
                        <span className="text-ink-muted">Colors</span>
                        <span className="font-medium">{colors.join(", ")}</span>
                      </div>
                    )}
                  </div>
                );
              }
              if (tab === "shipping") {
                return (
                  <ProductDeliveryInfo
                    isReturnable={product.isReturnable}
                    productName={product.name}
                    productUrl={`${SITE_URL}/product/${product.slug}`}
                  />
                );
              }
              return (
                <div className="max-w-2xl">
                  {reviews && reviews.length === 0 && <p className="text-ink-muted text-sm">No reviews yet.</p>}
                  <div className="flex flex-col gap-6">
                    {reviews?.map((r) => (
                      <div key={r.id} className="border-b border-border pb-6 last:border-b-0">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex text-accent">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star key={i} size={14} fill={i < r.rating ? "currentColor" : "none"} />
                            ))}
                          </div>
                          <span className="text-sm font-medium">{r.authorName}</span>
                          {r.isVerifiedPurchase && <Badge tone="success">Verified purchase</Badge>}
                        </div>
                        {r.text && <p className="text-ink-muted text-sm">{r.text}</p>}
                      </div>
                    ))}
                  </div>
                  {isAuthenticated && (
                    <form
                      className="mt-6 flex flex-col gap-3"
                      onSubmit={(e) => {
                        e.preventDefault();
                        createReview.mutate(
                          { rating: reviewRating, text: reviewText || undefined },
                          { onSuccess: () => setReviewText("") }
                        );
                      }}
                    >
                      <div role="group" aria-label="Your rating" className="flex gap-1">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <button
                            key={i}
                            type="button"
                            aria-label={`Rate ${i + 1} stars`}
                            aria-pressed={i < reviewRating}
                            onClick={() => setReviewRating(i + 1)}
                            className="text-accent"
                          >
                            <Star size={20} fill={i < reviewRating ? "currentColor" : "none"} />
                          </button>
                        ))}
                      </div>
                      <label htmlFor="review-text" className="sr-only">
                        Your review
                      </label>
                      <textarea
                        id="review-text"
                        value={reviewText}
                        onChange={(e) => setReviewText(e.target.value)}
                        placeholder="Share your thoughts on this product…"
                        rows={3}
                        className="rounded-xl border border-border bg-surface px-4 py-3 text-sm text-ink placeholder:text-ink-muted/60 focus:outline-none focus:ring-2 focus:ring-accent/40"
                      />
                      {createReview.isError && (
                        <p className="text-sm text-red-500">{(createReview.error as Error).message}</p>
                      )}
                      <button
                        type="submit"
                        disabled={createReview.isPending}
                        className="w-fit h-10 px-5 rounded-full bg-ink text-canvas text-sm font-medium hover:bg-accent hover:text-ink transition-colors duration-300 dark:bg-accent dark:text-ink"
                      >
                        {createReview.isPending ? "Submitting…" : "Submit review"}
                      </button>
                    </form>
                  )}
                </div>
              );
            }}
          </ProductTabs>
        </div>
      </div>

      <RelatedProducts categorySlug={product.category?.slug} excludeProductId={product.id} />
      <RecentlyViewedSection excludeSlug={product.slug} />

      {showStickyBar && (
        <div className="fixed bottom-0 left-0 right-0 glass border-t border-border p-4 flex items-center justify-between gap-4 z-30">
          <div>
            <p className="text-sm font-medium">{product.name}</p>
            <PriceTag cents={price} className="text-sm" />
          </div>
          <button
            type="button"
            disabled={!inStock || addToCart.isPending}
            onClick={handleAddToCart}
            className="h-11 px-6 rounded-full bg-ink text-canvas font-medium text-sm hover:bg-accent hover:text-ink transition-colors duration-300 ease-physical disabled:opacity-40 dark:bg-accent dark:text-ink"
          >
            {inStock ? "Add to cart" : "Out of stock"}
          </button>
        </div>
      )}
    </main>
  );
}
